/**
 * Universal Animation & Parallax System
 */

const DEFAULTS = {
  threshold: 0.12,
  delay: 0,
  staggerDelay: 100,
  heroAnimationOffset: 1400,
};

/**
 * Trigger animation on a single element.
 * Reads data-animate-delay (ms) and applies it as animation-delay.
 */
function triggerElement(el, extraDelay = 0) {
  const delay = parseInt(el.dataset.animateDelay ?? 0, 10) + extraDelay;
  el.style.animationDelay = `${delay}ms`;
  el.classList.add('is-animated');
}

/**
 * Trigger a stagger group: each child gets an increasing delay.
 */
function getInitialViewportDelay(el) {
  if (window.scrollY > 50) return 0;
  const section = el.closest('[data-animate-offset]');
  if (!section) return 0;
  return parseInt(section.dataset.animateOffset ?? 0, 10);
}

export function triggerStaggerGroup(wrapper, extraDelay = 0) {
  const staggerDelay = parseInt(
    wrapper.dataset.animateStaggerDelay ?? DEFAULTS.staggerDelay,
    10,
  );
  const children = Array.from(wrapper.children);

  children.forEach((child, i) => {
    const childDelay = parseInt(child.dataset.animateDelay ?? 0, 10);
    child.style.animationDelay = `${childDelay + i * staggerDelay + extraDelay}ms`;
    child.classList.add('is-animated');
  });

  wrapper.classList.add('is-animated');
}

/**
 * Universal Parallax System
 */
const initParallax = () => {
  const elements = document.querySelectorAll('[data-parallax]');
  if (elements.length === 0) return;

  let cachedElements = [];

  const cacheDimensions = () => {
    cachedElements = Array.from(elements).map((el) => {
      const parent = el.closest('.u-parallax-container') || el.parentElement;
      const parentRect = parent.getBoundingClientRect();
      const parentTop = parentRect.top + window.scrollY;
      const parentHeight = parentRect.height;
      const speed = parseFloat(el.dataset.parallaxSpeed) || 0.25;

      return {
        el,
        parentTop,
        parentHeight,
        speed,
      };
    });
  };

  // Cache dimensions initially and on window load/resize
  cacheDimensions();
  window.addEventListener('load', cacheDimensions, { passive: true });
  window.addEventListener('resize', cacheDimensions, { passive: true });

  let ticking = false;

  const updateParallax = () => {
    const vh = window.innerHeight;
    const scrollY = window.scrollY;

    cachedElements.forEach((item) => {
      const parentTopViewport = item.parentTop - scrollY;
      const parentBottomViewport = parentTopViewport + item.parentHeight;

      if (parentTopViewport < vh && parentBottomViewport > 0) {
        // Calculate progress: 0 when top of element is at bottom of viewport,
        // 1 when bottom of element is at top of viewport.
        const progress = (vh - parentTopViewport) / (vh + item.parentHeight);

        // Center the shift: 0 when element is in the middle of the viewport.
        const shift = (progress - 0.5) * (item.parentHeight * item.speed * 2);

        item.el.style.transform = `translate3d(0, ${shift}px, 0)`;
      }
    });

    ticking = false;
  };

  const onScroll = () => {
    if (!ticking) {
      window.requestAnimationFrame(updateParallax);
      ticking = true;
    }
  };

  window.addEventListener('scroll', onScroll, { passive: true });
  updateParallax(); // Initial positioning
};

/**
 * Scroll Reveal Effect
 * Changes top position as element touches viewport top
 */
const initScrollReveal = () => {
  const elements = document.querySelectorAll('[data-scroll-reveal]');
  if (elements.length === 0) return;

  let cachedElements = [];

  const cacheDimensions = () => {
    cachedElements = Array.from(elements).map((el) => {
      // Temporarily clear style.top to get initial bounding rect
      const prevTop = el.style.top;
      el.style.top = '';

      const rect = el.getBoundingClientRect();
      const elementTop = rect.top + window.scrollY;
      const elementHeight = rect.height;

      el.style.top = prevTop;

      return {
        el,
        elementTop,
        elementHeight,
      };
    });
  };

  // Cache dimensions initially and on window load/resize
  cacheDimensions();
  window.addEventListener('load', cacheDimensions, { passive: true });
  window.addEventListener('resize', cacheDimensions, { passive: true });

  let ticking = false;

  const updateScrollReveal = () => {
    const vh = window.innerHeight;
    const scrollY = window.scrollY;

    cachedElements.forEach((item) => {
      const distanceFromTop = item.elementTop - scrollY;

      // Increase maxOffset from -100 to -250 for a much more visible scrolling movement
      const maxOffset = -250;

      // When element touches or is near top of viewport
      if (distanceFromTop <= vh && distanceFromTop > -item.elementHeight) {
        // Calculate progress: 0 when top is at viewport bottom, 1 when at viewport top
        const progress = 1 - distanceFromTop / vh;

        // Apply negative top position that approaches 0 as we scroll up
        const offset = maxOffset + progress * Math.abs(maxOffset);

        item.el.style.top = `-${offset}px`;
      } else if (distanceFromTop > vh) {
        // Reset to initial state when below viewport
        item.el.style.top = '0';
      } else {
        // At or past top of viewport
        item.el.style.top = `${maxOffset}px`;
      }
    });

    ticking = false;
  };

  const onScroll = () => {
    if (!ticking) {
      window.requestAnimationFrame(updateScrollReveal);
      ticking = true;
    }
  };

  window.addEventListener('scroll', onScroll, { passive: true });
  updateScrollReveal(); // Initial positioning
};

/**
 * Initialize all animation observers and parallax
 */
export const initAnimations = () => {
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (prefersReducedMotion) return;

  // ── IntersectionObserver for data-animate elements ───────────────────────
  document.querySelectorAll('[data-animate]').forEach((el) => {
    // Skip children that are managed by a stagger parent
    if (el.closest('[data-animate-stagger]')) return;
    // Skip timeline items which are custom-managed for batching/staggering
    if (el.classList.contains('timeline__item')) return;

    const threshold = parseFloat(
      el.dataset.animateThreshold ?? DEFAULTS.threshold,
    );
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          triggerElement(entry.target, getInitialViewportDelay(entry.target));
          observer.unobserve(entry.target);
        });
      },
      { threshold },
    );
    observer.observe(el);
  });

  // ── IntersectionObserver for data-animate-stagger groups ─────────────────
  document.querySelectorAll('[data-animate-stagger]').forEach((el) => {
    const threshold = parseFloat(
      el.dataset.animateThreshold ?? DEFAULTS.threshold,
    );
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          triggerStaggerGroup(
            entry.target,
            getInitialViewportDelay(entry.target),
          );
          observer.unobserve(entry.target);
        });
      },
      { threshold },
    );
    observer.observe(el);
  });

  // ── Legacy support: .animate-on-scroll + .is-visible ─────────────────────
  const legacyObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        legacyObserver.unobserve(entry.target);
      });
    },
    { threshold: DEFAULTS.threshold },
  );

  document.querySelectorAll('.animate-on-scroll').forEach((el) => {
    legacyObserver.observe(el);
  });

  initParallax();
  initScrollReveal();
};
